import { ReactNode, createContext, useContext, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/16/solid';
import { cn } from '@/lib/utils';

interface TabsContextValue {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

const TabsContext = createContext<TabsContextValue | undefined>(undefined);

export function useTabsContext() {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tabs components must be used within Tabs');
  }
  return context;
}

export interface TabsProps {
  defaultTab?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  children: ReactNode;
  className?: string;
}

/**
 * Tabs - Sistema de pestañas con estilos Tailwind Plus
 * 
 * Mejoras aplicadas:
 * - Divider dark mode: border-white/5
 * - Hover underline effect
 * - Active tab con borde más prominente
 */
export function Tabs({ defaultTab, value, onValueChange, children, className = '' }: TabsProps) {
  const [internalTab, setInternalTab] = useState(defaultTab || '');
  
  // Use controlled value if provided, otherwise use internal state
  const activeTab = value !== undefined ? value : internalTab;
  const setActiveTab = (newTab: string) => {
    if (onValueChange) {
      onValueChange(newTab);
    } else {
      setInternalTab(newTab);
    }
  };

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}

export interface TabsListProps {
  children: ReactNode;
  className?: string;
}

export function TabsList({ children, className = '' }: TabsListProps) {
  return (
    <div className={cn(
      'border-b border-secondary-200 dark:border-white/5',
      className
    )}>
      <nav className="flex -mb-px gap-x-1">{children}</nav>
    </div>
  );
}

export interface TabsTriggerProps {
  value: string;
  children: ReactNode;
  className?: string;
  /** Ícono opcional */
  icon?: ReactNode;
}

export function TabsTrigger({ value, children, className = '', icon }: TabsTriggerProps) {
  const { activeTab, setActiveTab } = useTabsContext();
  const isActive = activeTab === value;

  return (
    <button
      type="button"
      onClick={() => setActiveTab(value)}
      className={cn(
        // Base
        'inline-flex items-center gap-2 px-4 py-3 text-sm font-medium',
        'border-b-2 transition-colors whitespace-nowrap',
        // Active state
        isActive ? cn(
          'border-primary-500 text-primary-600',
          'dark:border-primary-400 dark:text-primary-400'
        ) : cn(
          // Inactive - Tailwind Plus style con hover underline
          'border-transparent text-secondary-500',
          'hover:border-secondary-300 hover:text-secondary-700',
          'dark:text-secondary-400 dark:hover:border-white/20 dark:hover:text-secondary-300'
        ),
        className
      )}
      role="tab"
      aria-selected={isActive}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {children}
    </button>
  );
}

export interface TabsContentProps {
  value: string;
  children: ReactNode;
  className?: string;
}

export function TabsContent({ value, children, className = '' }: TabsContentProps) {
  const { activeTab } = useTabsContext();

  if (activeTab !== value) {
    return null;
  }

  return (
    <div className={cn('pt-4', className)} role="tabpanel">
      {children}
    </div>
  );
}

// ============================================
// Responsive Tabs (Tailwind Plus pattern)
// Select on mobile, tabs on desktop/tablet
// ============================================

export interface TabItem {
  value: string;
  label: string;
  icon?: string; // Emoji or icon string
}

export interface TabsSelectProps {
  tabs: TabItem[];
  /** Label shown before the select on mobile */
  selectLabel?: string;
  className?: string;
}

/**
 * TabsSelect - Select dropdown for mobile, hidden on sm+
 * Must be used inside a Tabs component
 */
export function TabsSelect({ tabs, selectLabel, className = '' }: TabsSelectProps) {
  const { activeTab, setActiveTab } = useTabsContext();

  return (
    <div className={cn('sm:hidden', className)}>
      {selectLabel && (
        <label className="block text-xs font-medium text-secondary-500 dark:text-secondary-400 mb-1 px-1">
          {selectLabel}
        </label>
      )}
      <div className="grid grid-cols-1">
        <select
          value={activeTab}
          onChange={(e) => setActiveTab(e.target.value)}
          aria-label={selectLabel || 'Select a section'}
          className={cn(
            'col-start-1 row-start-1 w-full appearance-none rounded-md',
            'py-2.5 pr-10 pl-3 text-base font-medium',
            // Light mode
            'bg-white text-secondary-900',
            'border border-secondary-300',
            'focus:border-primary-600 focus:ring-1 focus:ring-primary-600 focus:outline-none',
            // Dark mode
            'dark:bg-white/5 dark:text-secondary-100 dark:border-white/10',
            'dark:*:bg-secondary-800 dark:focus:border-primary-500 dark:focus:ring-primary-500'
          )}
        >
          {tabs.map((tab) => (
            <option key={tab.value} value={tab.value}>
              {tab.icon ? `${tab.icon} ${tab.label}` : tab.label}
            </option>
          ))}
        </select>
        <ChevronDownIcon
          aria-hidden="true"
          className={cn(
            'pointer-events-none col-start-1 row-start-1 mr-3 size-5',
            'self-center justify-self-end',
            'fill-secondary-500 dark:fill-secondary-400'
          )}
        />
      </div>
    </div>
  );
}

export interface TabsListResponsiveProps {
  children: ReactNode;
  className?: string;
}

/**
 * TabsListResponsive - Tabs list hidden on mobile, shown on sm+
 * For tablet, uses smaller text/padding
 */
export function TabsListResponsive({ children, className = '' }: TabsListResponsiveProps) {
  return (
    <div className={cn(
      'hidden sm:block',
      'border-b border-secondary-200 dark:border-white/10',
      className
    )}>
      <nav aria-label="Tabs" className="-mb-px flex space-x-4 lg:space-x-6">
        {children}
      </nav>
    </div>
  );
}

export interface TabsTriggerResponsiveProps {
  value: string;
  children: ReactNode;
  className?: string;
  /** Ícono (emoji o ReactNode) */
  icon?: ReactNode;
}

/**
 * TabsTriggerResponsive - Tab trigger with responsive sizing
 * Smaller on tablet (sm), normal on desktop (lg)
 */
export function TabsTriggerResponsive({ value, children, className = '', icon }: TabsTriggerResponsiveProps) {
  const { activeTab, setActiveTab } = useTabsContext();
  const isActive = activeTab === value;

  return (
    <button
      type="button"
      onClick={() => setActiveTab(value)}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        // Base - responsive sizing
        'inline-flex items-center gap-1.5 lg:gap-2',
        'px-1 py-3 lg:py-4',
        'text-xs sm:text-sm font-medium whitespace-nowrap',
        'border-b-2 transition-colors',
        // Active state
        isActive ? cn(
          'border-primary-500 text-primary-600',
          'dark:border-primary-400 dark:text-primary-400'
        ) : cn(
          // Inactive - Tailwind Plus style
          'border-transparent text-secondary-500',
          'hover:border-secondary-300 hover:text-secondary-700',
          'dark:text-secondary-400 dark:hover:border-white/20 dark:hover:text-secondary-200'
        ),
        className
      )}
      role="tab"
      aria-selected={isActive}
    >
      {icon && <span className="flex-shrink-0 text-base lg:text-lg">{icon}</span>}
      <span>{children}</span>
    </button>
  );
}
